import React, {useState, useEffect} from 'react';
import {useDebouncedCallback} from 'use-debounce';

import PriceInputs from '../PriceInputs/PriceInputs';
import {
    getLimitedMaxPrice,
    getLimitedMaxDisplayPrice,
} from '../../utils/priceUtils';
import Slider from '../Slider/Slider';

import {Props as FilterGroupContentProps} from '../FilterGroupContent/FilterGroupContent';
import PriceFilterByPriceType from '../PriceFilterByPriceType/PriceFilterByPriceType';

import './PriceFilterV2.less';

type Props = Pick<
    FilterGroupContentProps,
    | 'currency'
    | 'customFiltersParams'
    | 'onCustomFilterUpdate'
    | 'priceFilterOptions'
    | 'priceInputValuesAreValid'
    | 'updatePriceInputValuesValidity'
>;

export type BoundsType = Array<number | null>;

const normalizeBounds = (
    maxAllowedPrice: number,
    [minPrice, maxPrice]: BoundsType
): BoundsType => {
    const normalizedMinPrice = minPrice || 0;
    let normalizedMaxPrice;
    if (maxAllowedPrice && maxPrice && maxPrice >= maxAllowedPrice) {
        normalizedMaxPrice = null;
    } else {
        normalizedMaxPrice = maxPrice;
    }
    return [normalizedMinPrice, normalizedMaxPrice];
};

export default function PriceFilterV2({
    currency,
    customFiltersParams,
    onCustomFilterUpdate,
    priceFilterOptions = {},
    priceInputValuesAreValid,
    updatePriceInputValuesValidity,
}: Props) {
    const {
        appliedMinPrice,
        appliedMaxPrice,
        selectedMinPrice,
        selectedMaxPrice,
        selectedFilterByTotalPrice,
    } = customFiltersParams;
    const [activeHandleIndex, setActiveHandleIndex] = useState(null);
    const [internalPrice, setInteralPrice] = useState({
        minPrice: appliedMinPrice,
        maxPrice: appliedMaxPrice,
    });

    const [debouncedCallback] = useDebouncedCallback(onCustomFilterUpdate, 200);

    useEffect(() => {
        setInteralPrice({
            minPrice: selectedMinPrice,
            maxPrice: selectedMaxPrice,
        });
    }, [selectedMinPrice, selectedMaxPrice]);

    const minPriceDisplay = internalPrice.minPrice || 0;
    const maxAllowedPrice = getLimitedMaxPrice(
        currency,
        selectedFilterByTotalPrice,
        priceFilterOptions.lengthOfStay
    );

    const maxPriceDisplay = getLimitedMaxDisplayPrice(
        maxAllowedPrice,
        internalPrice.maxPrice
    );
    const isMax = maxPriceDisplay >= maxAllowedPrice;

    const handlePriceChange = (bounds: BoundsType) => {
        const [minPrice, maxPrice] = normalizeBounds(maxAllowedPrice, bounds);

        const newPriceValues = {
            minPrice,
            maxPrice,
        };

        setInteralPrice(newPriceValues);
        debouncedCallback(newPriceValues);
    };

    const handleFilterByPriceTypeChange = (filterByTotalPrice) => {
        onCustomFilterUpdate({filterByTotalPrice});
    };

    return (
        <div
            role="menuitem"
            aria-labelledby={`filter-group-label-PriceFilterV2`}
            className={'PriceFilterV2'}
        >
            {!!priceFilterOptions.showFilterByPriceType && (
                <PriceFilterByPriceType
                    isFilterByTotalPriceSelected={selectedFilterByTotalPrice}
                    isFilterByTotalPriceEnabled={
                        !!priceFilterOptions.isFilterByTotalPriceEnabled
                    }
                    onChange={handleFilterByPriceTypeChange}
                />
            )}

            <div
                className="PriceFilterV2__PriceInputs"
                data-wdio="PriceFilterV2__PriceInputs"
            >
                <PriceInputs
                    activeHandleIndex={activeHandleIndex}
                    currency={currency}
                    isMax={isMax}
                    maxAllowedPrice={maxAllowedPrice}
                    maxPrice={maxPriceDisplay}
                    minPrice={minPriceDisplay}
                    onChange={handlePriceChange}
                    priceInputValuesAreValid={priceInputValuesAreValid}
                    updatePriceInputValuesValidity={
                        updatePriceInputValuesValidity
                    }
                    useSeparatorBetweenInputs
                />
            </div>
            <div
                className="PriceFilterV2__slider"
                data-wdio="PriceFilterV2__slider"
            >
                <Slider
                    className={'Slider--min-max-price-fields-test'}
                    start={minPriceDisplay}
                    stop={maxPriceDisplay}
                    max={maxAllowedPrice}
                    activeHandleIndex={activeHandleIndex}
                    step={10}
                    onChange={handlePriceChange}
                    onSetActiveHandle={setActiveHandleIndex}
                    edapLabel="nightly_price_slider"
                />
            </div>
        </div>
    );
}
